import { isMediaPriceV111 } from './mediaPricePresentationV111';

export const MODEL_CATEGORY_FILTERS_V814 = [
  'all',
  'language',
  'image',
  'video',
  'audio',
];

export const MODEL_CATEGORY_META_V814 = {
  llm: { icon: 'comments outline', labelKey: 'models.catalog.type_llm' },
  vlm: { icon: 'eye', labelKey: 'models.catalog.type_vlm' },
  image: { icon: 'image outline', labelKey: 'models.catalog.type_image' },
  video: { icon: 'video', labelKey: 'models.catalog.type_video' },
  audio: { icon: 'volume up', labelKey: 'models.catalog.type_audio' },
};

export const MODEL_PROVIDER_META_V814 = {
  openai: { name: 'OpenAI', mark: 'OA' },
  anthropic: { name: 'Anthropic', mark: 'A' },
  google: { name: 'Google', mark: 'G' },
  deepseek: { name: 'DeepSeek', mark: 'DS' },
  meta: { name: 'Meta', mark: 'M' },
  xai: { name: 'xAI', mark: 'x' },
  mistral: { name: 'Mistral', mark: 'MI' },
  alibaba: { name: 'Alibaba Cloud', mark: 'Q' },
  stability: { name: 'Stability AI', mark: 'S' },
  cohere: { name: 'Cohere', mark: 'C' },
  microsoft: { name: 'Microsoft', mark: 'MS' },
  nvidia: { name: 'NVIDIA', mark: 'N' },
  amazon: { name: 'Amazon', mark: 'AWS' },
  moonshot: { name: 'Moonshot AI', mark: 'K' },
  zhipu: { name: 'Zhipu AI', mark: 'GLM' },
  minimax: { name: 'MiniMax', mark: 'MM' },
  bytedance: { name: 'ByteDance', mark: 'BD' },
  custom: { name: 'Custom', mark: 'AI' },
};

const normalizeList = (value) =>
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

const priceList = (item) =>
  Array.isArray(item?.customer_prices) ? item.customer_prices : [];

const modelIdentity = (item) =>
  `${item?.model_name || ''} ${item?.display_name || ''}`.toLocaleLowerCase();

const hasAny = (value, expressions) =>
  expressions.some((expression) => expression.test(value));

export const inferModelProviderV814 = (item) => {
  const identity = modelIdentity(item);
  const matches = [
    ['anthropic', [/claude/]],
    ['google', [/gemini/, /imagen/, /\bveo[-_. ]/, /chirp/, /lyria/]],
    ['deepseek', [/deepseek/]],
    ['meta', [/llama/]],
    ['xai', [/grok/]],
    ['mistral', [/mistral/, /mixtral/, /codestral/, /pixtral/]],
    ['alibaba', [/qwen/, /qwq/, /\bwan[-_. ]/]],
    ['stability', [/stable[-_. ]?diffusion/, /\bsd3\b/]],
    ['cohere', [/command[-_. ]?r/, /\baya\b/, /cohere/]],
    ['microsoft', [/\bphi[-_. ]/]],
    ['nvidia', [/nemotron/, /nvidia/]],
    ['amazon', [/amazon[-_. ]?nova/, /\bnova[-_. ]/]],
    ['moonshot', [/kimi/, /moonshot/]],
    ['zhipu', [/\bglm[-_. ]/, /cogvideo/, /cogview/]],
    ['minimax', [/minimax/, /hailuo/]],
    ['bytedance', [/doubao/, /seedance/, /seedream/]],
    [
      'openai',
      [
        /(^|\s|[/_.-])gpt[-_. ]/,
        /(^|\s|[/_.-])o[134][-.]/,
        /dall[-_. ]?e/,
        /\bsora\b/,
        /whisper/,
        /openai/,
      ],
    ],
  ];
  const match = matches.find(([, expressions]) =>
    hasAny(identity, expressions)
  );
  return match ? match[0] : 'custom';
};

const fallbackGenerationCategory = (identity) => {
  if (
    hasAny(identity, [
      /\bveo[-_. ]/,
      /\bsora\b/,
      /seedance/,
      /cogvideo/,
      /\bwan[-_. ]/,
      /video/,
    ])
  ) {
    return 'video';
  }
  if (
    hasAny(identity, [/whisper/, /chirp/, /lyria/, /tts/, /speech/, /audio/])
  ) {
    return 'audio';
  }
  if (
    hasAny(identity, [
      /imagen/,
      /dall[-_. ]?e/,
      /flux/,
      /stable[-_. ]?diffusion/,
      /seedream/,
      /cogview/,
      /image/,
    ])
  ) {
    return 'image';
  }
  return 'llm';
};

export const classifyModelV814 = (item) => {
  const modelType = String(item?.model_type || '').trim();
  const capabilities = new Set(normalizeList(item?.capabilities));
  const meters = new Set(priceList(item).map((price) => price.meter));
  const categories = new Set();

  const quotes = Array.isArray(item?.source_quotes) ? item.source_quotes : [];
  const offers = Array.isArray(item?.offers) ? item.offers : [];
  const mediaKinds = new Set(
    [...priceList(item), ...quotes.flatMap(priceList), ...offers.flatMap(priceList)]
      .filter(isMediaPriceV111)
      .map((price) => price.attributes?.media_kind)
  );
  for (const kind of ['video', 'image', 'audio', 'text']) {
    if (mediaKinds.has(kind)) {
      categories.add(
        kind === 'text' ? (capabilities.has('image_input') ? 'vlm' : 'llm') : kind
      );
    }
  }

  const hasText = modelType === 'text_generation' || capabilities.has('text');
  if (hasText || capabilities.has('image_input')) {
    categories.add(capabilities.has('image_input') ? 'vlm' : 'llm');
  }
  if (
    modelType === 'native_image_generation' ||
    capabilities.has('image_generation') ||
    meters.has('image') ||
    meters.has('image_output_tokens')
  ) {
    categories.add('image');
  }
  if (
    modelType === 'video_generation' ||
    meters.has('video') ||
    meters.has('video_duration')
  ) {
    categories.add('video');
  }
  if (
    modelType === 'audio_generation' ||
    meters.has('audio_characters') ||
    meters.has('audio_duration')
  ) {
    categories.add('audio');
  }

  if (categories.size === 0) {
    categories.add(fallbackGenerationCategory(modelIdentity(item)));
  }
  return [...categories];
};

export const hasThinkingV814 = (item) =>
  normalizeList(item?.capabilities).includes('thinking') ||
  priceList(item).some((price) => price.meter === 'thinking_tokens');

const numericAttribute = (attributes, key) => {
  const value = Number(attributes?.[key]);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const contextRank = (price) => {
  const attributes = price?.attributes || {};
  const minimum = numericAttribute(attributes, 'context_min_tokens');
  const maximum = numericAttribute(attributes, 'context_max_tokens');
  if (minimum === null && maximum === null) return [0, 0, 0];
  return [1, minimum || 0, maximum || Number.MAX_SAFE_INTEGER];
};

const compareSummaryPrice = (left, right) => {
  const leftTier = String(left?.attributes?.service_tier || '').toLowerCase();
  const rightTier = String(right?.attributes?.service_tier || '').toLowerCase();
  const leftTierRank = leftTier === '' || leftTier === 'standard' ? 0 : 1;
  const rightTierRank = rightTier === '' || rightTier === 'standard' ? 0 : 1;
  if (leftTierRank !== rightTierRank) return leftTierRank - rightTierRank;
  const leftContext = contextRank(left);
  const rightContext = contextRank(right);
  for (let index = 0; index < leftContext.length; index += 1) {
    if (leftContext[index] !== rightContext[index]) {
      return leftContext[index] - rightContext[index];
    }
  }
  return (
    Number(left?.unit_amount_usd || 0) - Number(right?.unit_amount_usd || 0)
  );
};

const METER_PRIORITY_V814 = [
  'input_tokens',
  'input_text_tokens',
  'output_tokens',
  'output_text_tokens',
  'image',
  'image_output_tokens',
  'video',
  'video_duration',
  'audio_characters',
  'audio_duration',
  'cached_input_tokens',
  'cache_storage_tokens',
  'cache_creation_tokens',
  'reference_images',
  'thinking_tokens',
  'input_image_tokens',
  'input_audio_tokens',
  'input_video_tokens',
  'input_document_tokens',
];

const meterPriority = (meter) => {
  const index = METER_PRIORITY_V814.indexOf(meter);
  return index === -1 ? METER_PRIORITY_V814.length : index;
};

export const selectBaseCustomerPricesV814 = (prices) => {
  const byMeter = new Map();
  (Array.isArray(prices) ? prices : []).forEach((price) => {
    const meter = String(price?.meter || '').trim();
    if (!meter) return;
    const minutes =
      price?.attributes?.ttl_minutes ||
      { cache_creation_5m_tokens: 5, cache_creation_1h_tokens: 60 }[meter];
    const key = minutes ? `cache_creation_tokens:${minutes}` : meter;
    const candidates = byMeter.get(key) || [];
    candidates.push(price);
    byMeter.set(key, candidates);
  });
  return [...byMeter.entries()]
    .map(([, candidates]) => [...candidates].sort(compareSummaryPrice)[0])
    .sort(
      (left, right) =>
        meterPriority(left.meter) - meterPriority(right.meter) ||
        left.meter.localeCompare(right.meter) ||
        Number(left.attributes?.ttl_minutes || 0) -
          Number(right.attributes?.ttl_minutes || 0)
    );
};

export const matchesModelFiltersV814 = (
  item,
  categoryFilter = 'all',
  groupFilter = ''
) => {
  const categories = classifyModelV814(item);
  const categoryMatches =
    categoryFilter === 'all' ||
    (categoryFilter === 'language'
      ? categories.includes('llm') || categories.includes('vlm')
      : categories.includes(categoryFilter));
  const groupMatches =
    !groupFilter || normalizeList(item?.groups).includes(groupFilter);
  return categoryMatches && groupMatches;
};

export const buildModelPresentationV814 = (item) => {
  const providerKey = inferModelProviderV814(item);
  const prices = priceList(item);
  return {
    providerKey,
    provider: MODEL_PROVIDER_META_V814[providerKey],
    categories: classifyModelV814(item),
    thinking: hasThinkingV814(item),
    prices,
    basePrices: selectBaseCustomerPricesV814(prices),
    offers: Array.isArray(item?.offers) ? item.offers : [],
    sourceDirectoryV107: Boolean(item?.source_directory_v107),
    sourceQuotes: Array.isArray(item?.source_quotes) ? item.source_quotes : [],
  };
};
